/**
 * Signalen (sessie 47, deel 3e; sessie 48, E3).
 *
 * Technische signalen horen niet tussen de klantberichten: een
 * checkoutanker dat ontbreekt, een themabestand dat veranderde, een
 * bewakingsstoring. Eén kaart per signaal met de datum, wat er mis is, het
 * patroon (staafje over zeven dagen en de norm) en op dezelfde plek de
 * knoppen om het af te handelen: gezien, hersteld of referentie
 * gelijkzetten, altijd met een reden. Afgehandeld is terug te draaien met
 * Heropen. Open signalen bovenaan; wat de laatste week af is eronder,
 * gedempt, met de reden.
 *
 * Sessie 161: ook de interne taken (een actie aan een zaak zonder klantgesprek,
 * zoals "Wachtwoordlink sturen mislukt") staan hier, niet meer in Voor jou. Knop
 * Gedaan met een reden, en Naar zaak opent de zaak op Vandaag.
 *
 * Sessie 61: daaronder een eigen blok "Bijna op deze week", de klanten
 * van wie het voer volgens het verbruiksmodel binnen zeven dagen op is.
 * Dat is geen storing: geen open of afgehandeld, alleen een lijst. Het
 * draait droog; er gaat geen bericht naar de klant.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

import { vertaalApiFout } from "@/lib/api";
import { signalenApi, type SignaalKaart } from "@/lib/werkplek-api";

import { MetCopiloot } from "./Copiloot/MetCopiloot";
import "./Vandaag/vandaag.css";

const ACTIE_LABEL: Record<string, string> = {
  gezien: "Gezien",
  hersteld: "Hersteld",
  referentie: "Referentie gelijkzetten",
  // Sessie 161: een interne taak (bron "taak") die niet meer in Voor jou staat.
  gedaan: "Gedaan",
};

function datum(iso: string): string {
  return new Date(iso).toLocaleString("nl-NL", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Amsterdam",
  });
}

function foutTekst(fout: unknown): string {
  const data = (fout as { response?: { data?: { detail?: unknown } } })?.response
    ?.data;
  if (data && typeof data.detail === "string") return data.detail;
  return "Afhandelen is niet gelukt. Probeer het opnieuw.";
}

/** "2026-10-01" wordt "do 1 okt", zonder tijdzone-verschuiving. */
function dagKort(waarde: string): string {
  const alleenDag = /^(\d{4})-(\d{2})-(\d{2})$/.exec(waarde);
  const moment = alleenDag
    ? new Date(
        Date.UTC(
          Number(alleenDag[1]),
          Number(alleenDag[2]) - 1,
          Number(alleenDag[3]),
        ),
      )
    : new Date(waarde);
  if (Number.isNaN(moment.getTime())) return waarde;
  return moment.toLocaleDateString("nl-NL", {
    timeZone: alleenDag ? "UTC" : "Europe/Amsterdam",
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

/** Sessie 61: klanten van wie het voer deze week op is. Droogstand. */
function BijnaOpBlok() {
  const bijnaOp = useQuery({
    queryKey: ["werkplek", "signalen", "bijna-op", 7],
    queryFn: () => signalenApi.bijnaOp(7),
  });
  const regels = bijnaOp.data?.regels ?? [];

  return (
    <section className="wp-blok" data-testid="wp-bijna-op" style={{ marginTop: 16 }}>
      <h4>
        <span data-testid="wp-bijna-op-kop">
          Bijna op deze week
          {bijnaOp.data ? ` \u00b7 ${bijnaOp.data.aantal}` : ""}
        </span>
      </h4>
      <div className="wp-blok-body">
        <p style={{ margin: "0 0 8px", fontSize: 12, color: "var(--wp-tekst3)" }}>
          Droogstand: er worden geen berichten gestuurd, dit is alleen de lijst.
          {bijnaOp.data?.berekend_op
            ? ` Berekend ${datum(bijnaOp.data.berekend_op)}.`
            : ""}
        </p>
        {bijnaOp.isLoading ? (
          <p style={{ margin: 0, color: "var(--wp-tekst2)" }}>Bezig met laden…</p>
        ) : bijnaOp.isError ? (
          <p role="alert" style={{ margin: 0, color: "var(--wp-rood)", overflowWrap: "anywhere" }}>
            De lijst is niet op te halen: {vertaalApiFout(bijnaOp.error)}
          </p>
        ) : regels.length === 0 ? (
          <p style={{ margin: 0, color: "var(--wp-tekst2)" }}>
            Deze week raakt bij geen enkele klant het voer op, voor zover het
            model het kan zeggen.
          </p>
        ) : (
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {regels.map((regel, index) => (
              <li
                key={`${regel.klant_id}-${regel.product_naam}-${index}`}
                data-testid="wp-bijna-op-regel"
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "baseline",
                  gap: "2px 10px",
                  padding: "6px 0",
                  borderBottom: "1px solid var(--wp-lijn2)",
                  fontSize: 13,
                }}
              >
                <Link
                  to={`/klanten/${regel.klant_id}`}
                  style={{ fontWeight: 600, minWidth: 0, overflowWrap: "anywhere" }}
                >
                  {regel.klant_naam}
                </Link>
                <span style={{ minWidth: 0, flex: "1 1 160px", overflowWrap: "anywhere", color: "var(--wp-tekst2)" }}>
                  {regel.product_naam}
                </span>
                <span style={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                  {dagKort(regel.op_datum)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function Staafje({ signaal }: { signaal: SignaalKaart }) {
  const staven = signaal.staafje ?? [];
  if (staven.length === 0) return null;
  const hoogste = Math.max(1, ...staven.map((s) => s.waarde));
  return (
    <div className="wp-staafje" data-testid="wp-signaal-staafje" aria-label="Zeven dagen">
      {staven.map((staaf) => (
        <span
          key={staaf.dag}
          className="wp-staaf"
          data-boven={staaf.boven_norm ? "ja" : "nee"}
          title={`${staaf.dag}: ${staaf.tekst}`}
          style={{ height: `${Math.max(2, Math.round((staaf.waarde / hoogste) * 28))}px` }}
        />
      ))}
    </div>
  );
}

function Afhandelen({ signaal }: { signaal: SignaalKaart }) {
  const cache = useQueryClient();
  const [keuze, setKeuze] = useState<string | null>(null);
  const [reden, setReden] = useState("");
  const ververs = () => {
    void cache.invalidateQueries({ queryKey: ["werkplek", "signalen"] });
    void cache.invalidateQueries({ queryKey: ["werkplek", "menutellers"] });
  };
  const afhandelen = useMutation({
    mutationFn: () =>
      signalenApi.afhandelen(signaal.bron, signaal.id, keuze as string, reden.trim()),
    onSuccess: () => {
      setKeuze(null);
      setReden("");
      ververs();
    },
  });
  const heropen = useMutation({
    mutationFn: () => signalenApi.heropen(signaal.bron, signaal.id),
    onSuccess: ververs,
  });

  if (!signaal.open) {
    return (
      <div className="wp-signaal-af" data-testid="wp-signaal-af">
        <span>
          {signaal.afgehandeld_reden ?? "Hersteld"}
          {signaal.afgehandeld_op ? ` · ${datum(signaal.afgehandeld_op)}` : null}
        </span>
        {signaal.afgehandeld_reden ? (
          <button
            type="button"
            className="wp-knop"
            data-testid="wp-signaal-heropen"
            onClick={() => heropen.mutate()}
            disabled={heropen.isPending}
          >
            Heropen
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="wp-signaal-knoppen">
      {(signaal.acties ?? []).map((actie) => (
        <button
          key={actie}
          type="button"
          className="wp-knop"
          aria-pressed={keuze === actie}
          data-testid={`wp-signaal-${actie}`}
          onClick={() => setKeuze(keuze === actie ? null : actie)}
        >
          {ACTIE_LABEL[actie] ?? actie}
        </button>
      ))}
      {signaal.bron_pad ? (
        <Link className="wp-knop" to={signaal.bron_pad} data-testid="wp-signaal-bron">
          Naar {signaal.bron_label}
        </Link>
      ) : null}
      {keuze ? (
        <form
          className="wp-signaal-reden"
          onSubmit={(e) => {
            e.preventDefault();
            if (reden.trim()) afhandelen.mutate();
          }}
        >
          <label htmlFor={`reden-${signaal.id}`}>
            Reden ({ACTIE_LABEL[keuze]?.toLowerCase() ?? keuze})
          </label>
          <textarea
            id={`reden-${signaal.id}`}
            data-testid="wp-signaal-reden"
            value={reden}
            onChange={(e) => setReden(e.target.value)}
            rows={2}
            placeholder={
              keuze === "referentie"
                ? "Bijvoorbeeld: bedoelde plaatsing r1b17 van 21-09."
                : keuze === "gedaan"
                  ? "Wat heb je gedaan?"
                  : "Wat heb je gezien of hersteld?"
            }
          />
          <div className="wp-signaal-reden-knoppen">
            <button
              type="submit"
              className="wp-knop wp-knop-primair"
              data-testid="wp-signaal-bevestig"
              disabled={!reden.trim() || afhandelen.isPending}
              title={reden.trim() ? undefined : "Vul eerst een reden in."}
            >
              {ACTIE_LABEL[keuze]} en afhandelen
            </button>
            {!reden.trim() ? (
              <span className="wp-leegregel">Een reden is verplicht.</span>
            ) : null}
          </div>
          {afhandelen.isError ? (
            <p role="alert" className="wp-signaal-fout">
              {foutTekst(afhandelen.error)}
            </p>
          ) : null}
        </form>
      ) : null}
    </div>
  );
}

export function WerkplekSignalen() {
  const lijst = useQuery({
    queryKey: ["werkplek", "signalen"],
    queryFn: signalenApi.lijst,
    refetchInterval: 60_000,
  });
  const open = (lijst.data ?? []).filter((s) => s.open);
  const af = (lijst.data ?? []).filter((s) => !s.open);

  return (
    <>
      <div className="wp-bovenbalk">
        <h1 style={{ fontSize: 17, margin: 0, fontWeight: 600 }}>Signalen</h1>
        {lijst.data ? (
          <span style={{ color: "var(--wp-tekst2)", fontSize: 13 }}>
            {open.length === 1 ? "1 open signaal" : `${open.length} open signalen`}
          </span>
        ) : null}
      </div>
      <div className="wp-inhoud">
        <MetCopiloot pagina="signalen">
          <div
            style={{ overflowY: "auto", padding: 18, maxWidth: 900, width: "100%", marginInline: "auto" }}
            data-testid="wp-signalen"
          >
            {lijst.isLoading ? (
              <p style={{ color: "var(--wp-tekst2)" }}>Bezig met laden…</p>
            ) : lijst.isError ? (
              <p role="alert" style={{ color: "var(--wp-rood)" }}>
                De signalen zijn niet op te halen. Ververs de pagina; blijft dit
                staan, dan is de API niet bereikbaar.
              </p>
            ) : (lijst.data ?? []).length === 0 ? (
              <div className="wp-blok" style={{ padding: 18 }}>
                <h2 style={{ margin: "0 0 6px", fontSize: 15 }}>Geen signalen</h2>
                <p style={{ margin: 0, color: "var(--wp-tekst2)" }}>
                  Geen checkoutankers, themabestanden of storingen die aandacht
                  vragen, en de laatste week niets afgehandeld.
                </p>
              </div>
            ) : (
              [...open, ...af].map((s) => (
                <div
                  key={`${s.bron}-${s.id}`}
                  className="wp-blok wp-signaal"
                  data-open={s.open ? "ja" : "nee"}
                  data-testid="wp-signaal"
                  style={{ padding: "12px 14px", marginBottom: 10, opacity: s.open ? 1 : 0.7 }}
                >
                  <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                    <span className={`wp-chip ${s.open ? "wp-chip-risico" : "wp-chip-grijs"}`}>
                      {s.open ? "open" : "afgehandeld"}
                    </span>
                    <strong style={{ fontSize: 14, minWidth: 0, overflowWrap: "anywhere" }}>
                      {s.titel}
                    </strong>
                    <span style={{ marginLeft: "auto", color: "var(--wp-tekst2)", fontSize: 12 }}>
                      {datum(s.moment)}
                    </span>
                  </div>
                  {s.patroon || (s.staafje ?? []).length > 0 || s.norm ? (
                    <div className="wp-signaal-patroon" data-testid="wp-signaal-patroon">
                      <Staafje signaal={s} />
                      <div style={{ minWidth: 0 }}>
                        {s.patroon ? <div>{s.patroon}</div> : null}
                        {s.norm ? (
                          <div className="wp-leegregel" data-testid="wp-signaal-norm">
                            Norm: {s.norm}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                  {s.wat ? (
                    <details className="wp-signaal-wat">
                      <summary>Wat er gemeten is</summary>
                      <p style={{ margin: "6px 0 8px", fontSize: 13, whiteSpace: "pre-line", overflowWrap: "anywhere" }}>
                        {s.wat}
                      </p>
                    </details>
                  ) : null}
                  <Afhandelen signaal={s} />
                </div>
              ))
            )}
            <BijnaOpBlok />
          </div>
        </MetCopiloot>
      </div>
    </>
  );
}
